import { Helmet } from 'react-helmet-async';
import { SITE, canonicalUrl } from '../config/site';

const PageSEO = ({
  title,
  description,
  path = '',
  ogTitle,
  ogDescription,
  noindex = false,
  ogType = 'website',
  // Only for routes that don't correspond to a real resource (invalid
  // :id/:slug, catch-all 404). A self-canonical there would claim the
  // requested (non-existent) URL is the canonical version of itself — a
  // false signal. Real pages that are merely noindexed for now (e.g. thin
  // content pending expansion) should keep their self-canonical as usual;
  // noindex and "no canonical" are independent decisions.
  omitCanonical = false,
  // [{ hreflang: 'fi'|'ru'|'en', path }] -- real language alternates of
  // THIS page only (must include a self-referencing entry for `path`'s own
  // language; see Google's hreflang spec). Callers must only pass entries
  // for versions that actually exist -- never fabricate one. Empty/omitted
  // when a page has no real translations (a lone self-reference has no
  // SEO value and is deliberately omitted rather than emitted alone).
  alternates = [],
  // hreflang="x-default" target, only when the caller has an actual
  // default-language version to point to (this site's fi, unprefixed
  // locale -- see scripts/lib/routes.js BLOG_LOCALE_PREFIXES). Ignored
  // unless `alternates` is also non-empty.
  xDefaultPath,
  // Absolute URL of THIS page's own social-preview image (og:image /
  // twitter:image) -- e.g. a blog article's own cover
  // (absoluteAssetUrl(article.coverImage) in BlogArticlePage.jsx). Falls
  // back to the sitewide PixelCraft OG image for every page that doesn't
  // have (or pass) one of its own, so a social crawler never has to fall
  // through to nothing.
  image = SITE.ogImage.url,
}) => {
  const canonical = canonicalUrl(path);
  const resolvedTitle = ogTitle || title;
  const resolvedDesc = ogDescription || description;

  return (
    <Helmet>
      <title>{title}</title>
      {description && <meta name="description" content={description} />}
      <meta name="robots" content={noindex ? 'noindex,follow' : 'index,follow'} />
      {!omitCanonical && <link rel="canonical" href={canonical} />}
      {alternates.map(({ hreflang, path: altPath }) => (
        <link key={hreflang} rel="alternate" hreflang={hreflang} href={canonicalUrl(altPath)} />
      ))}
      {alternates.length > 0 && xDefaultPath && (
        <link rel="alternate" hreflang="x-default" href={canonicalUrl(xDefaultPath)} />
      )}

      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={SITE.name} />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={resolvedTitle} />
      {resolvedDesc && <meta property="og:description" content={resolvedDesc} />}
      <meta property="og:image" content={image} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={resolvedTitle} />
      {resolvedDesc && <meta name="twitter:description" content={resolvedDesc} />}
      <meta name="twitter:image" content={image} />
    </Helmet>
  );
};

export default PageSEO;
